import moment from 'moment-timezone'
import { get } from 'lodash'
import React, { useMemo } from 'react'
import { makeStyles } from '@material-ui/core'

import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import { Opportunity } from 'Event/interfaces'
import CalendarHeader from './CalendarHeader'
import EventCalendarDay from './EventCalendarDay'
import EventPreviewPrompt from './EventPreviewPrompt'
import { useEventCalendarGrid } from './hooks/useEventCalendarGrid'
import { EventListDifferences } from 'Event/components/EventListDifferences'
import { useCurrentOrg } from 'civic-champs-shared/auth/hooks'

const useStyles = makeStyles(theme => ({
  calendarContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: theme.palette.background.default,
    boxShadow: theme.shadows[4],
    borderRadius: 4,
    padding: '25px 10px 16px 10px',
    flex: '1 1 auto',
    [theme.breakpoints.down('sm')]: {
      padding: 0,
      borderRadius: 0,
      boxShadow: 'none',
    },
  },
  calendarBodyContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    [theme.breakpoints.down('sm')]: {
      border: '1px solid #cfd7e8',
    },
  },
  calendarWeek: {
    display: 'flex',
    flex: '1 1 auto',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
}))

interface Props {
  events: Opportunity[]
  date: number
  month: number
  year: number
  viewType: string
}

//copied from 'useEventCalendarGrid()', used for comparison view
const useCalendarRange = ({ date, month, year, viewType }: Props) => {
  const organization = useCurrentOrg()
  const timeZone = get(organization, 'timeZone', 'America/New_York')

  return useMemo(
    () => ({
      start: moment
        .tz([year, month, date], timeZone)
        .startOf(viewType as any)
        .weekday(0),
      end: moment
        .tz([year, month, date], timeZone)
        .endOf(viewType as any)
        .weekday(6),
    }),
    [year, month, date, timeZone, viewType],
  )
}

const EventCalendar = (props: Props) => {
  const { date, month, year, events, viewType } = props
  const classes = useStyles()
  const grid = useEventCalendarGrid(date, month, year, events, viewType)

  const showPreview = useShowPrompt(EventPreviewPrompt)

  const { start, end } = useCalendarRange(props)

  return (
    <div className={classes.calendarContainer}>
      <CalendarHeader />
      <div className={classes.calendarBodyContainer}>
        {grid.map((row, week) => (
          <div key={week} className={classes.calendarWeek}>
            {row.map((cell, day) => (
              <EventCalendarDay key={day} {...cell} onShowEventPopup={showPreview} month={month} viewType={viewType} />
            ))}
          </div>
        ))}
      </div>
      <EventListDifferences from={start} to={end} />
    </div>
  )
}

export default EventCalendar
